/**
 * AI Flows Service
 *
 * Provides functions for interacting with the AI Flows API through a secure Edge Function proxy.
 * All requests go through the aiflows-proxy edge function which handles authentication
 * and keeps API keys secure on the server side.
 */

import { supabase } from './supabase';

/**
 * Makes a request to the AI Flows proxy edge function
 * @param {string} action - The action to perform
 * @param {string} companyId - The company ID
 * @param {Object} params - Additional parameters for the action
 * @returns {Promise<Object>} The response data
 */
async function proxyRequest(action, companyId, params = {}) {
  const { data, error } = await supabase.functions.invoke('aiflows-proxy', {
    body: {
      action,
      companyId,
      ...params
    }
  });

  if (error) {
    console.error('AI Flows proxy error:', error);
    throw new Error(error.message || 'Failed to connect to AI Flows service');
  }

  if (data?.error) {
    throw new Error(data.error.message || 'AI Flows API error');
  }

  return data;
}

/**
 * Validates an API key by testing the connection to AI Flows
 * @param {string} companyId - The company ID
 * @param {string} apiKey - The API key to validate
 * @param {string} workspaceName - Optional workspace name
 * @returns {Promise<{valid: boolean, error?: string, saved?: boolean}>}
 */
export async function validateKey(companyId, apiKey, workspaceName = '') {
  return proxyRequest('validate-key', companyId, { apiKey, workspaceName });
}

/**
 * Gets the integration status for a company
 * @param {string} companyId - The company ID
 * @returns {Promise<{isConfigured: boolean, integration?: Object}>}
 */
export async function getIntegration(companyId) {
  return proxyRequest('get-integration', companyId);
}

/**
 * Removes the integration for a company
 * @param {string} companyId - The company ID
 * @returns {Promise<{success: boolean}>}
 */
export async function removeIntegration(companyId) {
  return proxyRequest('remove-integration', companyId);
}

/**
 * Gets the list of flows for the company's workspace
 * @param {string} companyId - The company ID
 * @returns {Promise<Array<{id: string, name: string, webhook_url: string}>>}
 */
export async function getFlows(companyId) {
  const result = await proxyRequest('get-flows', companyId);
  return result.data || [];
}

/**
 * Gets details of a specific flow
 * @param {string} companyId - The company ID
 * @param {string} flowId - The flow ID
 * @returns {Promise<{id: string, name: string, webhook_url: string, description?: string}>}
 */
export async function getFlow(companyId, flowId) {
  const result = await proxyRequest('get-flow', companyId, { flowId });
  return result.data;
}

/**
 * Triggers a flow webhook
 * @param {string} companyId - The company ID
 * @param {string} flowId - The flow ID
 * @param {Object} payload - The data to send to the flow
 * @param {boolean} waitForCompletion - Whether to wait for the flow to complete
 * @returns {Promise<{success: boolean, executionId?: string, result?: any}>}
 */
export async function triggerFlow(companyId, flowId, payload = {}, waitForCompletion = false) {
  return proxyRequest('trigger-flow', companyId, {
    flowId,
    payload,
    waitForCompletion
  });
}

/**
 * Checks the status of a flow execution
 * @param {string} companyId - The company ID
 * @param {string} executionId - The execution ID
 * @returns {Promise<{status: string, result?: any}>}
 */
export async function getExecutionStatus(companyId, executionId) {
  return proxyRequest('get-execution-status', companyId, { executionId });
}
